import React, { useState, useEffect } from 'react';
import { Menu, X, Mail, Heart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Accueil', href: '/' },
    { label: 'A Propos', href: '/about' },
    { label: 'Nos Axes', href: '/interventions' },
    { label: 'Programmes', href: '/programmes' },
    { label: 'Actualites', href: '/actualites' },
    { label: 'Equipe', href: '/team' },
    { label: 'Galerie', href: '/galerie' },
  ];

  const handleSupportClick = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.querySelector('#contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3 group" data-testid="header-logo">
            <img src="/images/logo-hpd.png" alt="HPD Logo" className="h-12 w-12 object-contain transition-transform group-hover:scale-105" />
            <div translate="no" className="notranslate">
              <h1 className={`font-sans font-bold text-lg leading-tight transition-colors ${isScrolled ? 'text-navy' : 'text-white'}`}>
                Hope for Peace
              </h1>
              <p className={`text-xs font-sans transition-colors ${isScrolled ? 'text-forest' : 'text-white/80'}`}>
                & Development
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                className={`px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === link.href
                    ? isScrolled
                      ? 'bg-navy/10 text-navy'
                      : 'bg-white/20 text-white'
                    : isScrolled
                    ? 'text-gray-600 hover:text-navy hover:bg-navy/5'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/don"
              data-testid="nav-donate-btn"
              className="ml-3 inline-flex items-center px-5 py-2.5 rounded-full bg-forest text-white text-sm font-semibold hover:bg-forest/90 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Heart className="w-4 h-4 mr-2" fill="currentColor" />
              Faire un don
            </Link>
            <a
              href="https://mail.hostinger.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="webmail-btn"
              title="WebMail"
              className={`ml-2 p-2.5 rounded-full transition-all duration-300 ${
                isScrolled ? 'text-navy hover:bg-navy/5' : 'text-white hover:bg-white/10'
              }`}
            >
              <Mail className="w-4 h-4" />
            </a>
          </nav>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2.5 rounded-full transition-colors ${
              isScrolled ? 'text-navy hover:bg-navy/5' : 'text-white hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 overflow-hidden"
          >
            <nav className="px-4 py-6 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'bg-navy/10 text-navy'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 space-y-2">
                <Link
                  to="/don"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center w-full px-4 py-3 rounded-xl bg-forest text-white font-semibold"
                  data-testid="mobile-donate-btn"
                >
                  <Heart className="w-4 h-4 mr-2" fill="currentColor" />
                  Faire un don
                </Link>
                <button
                  onClick={handleSupportClick}
                  className="flex items-center justify-center w-full px-4 py-3 rounded-xl bg-navy text-white font-semibold"
                >
                  Nous contacter
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
